import React, {useEffect, useState} from 'react'
import AsyncSelect from 'react-select/async'
import PropTypes from 'prop-types'

import _concat from 'lodash/concat'
import _sortBy from 'lodash/sortBy'
import _ from 'lodash'
import debounce from 'debounce-promise'
import {fetchGroups, fetchUsers, getUserById} from '../../utils/services'
import PapiUser from '../user/PapiUser'
import PapiGroup from '../group/PapiGroup'

import LoadingIndicator from '../shared/LoadingIndicator'
import DropdownIndicator from '../shared/DropdownIndicator'
import FloatingLabel from '../shared/FloatingLabel'
import useFocussedPlaceholder from '../../utils/useFocussedPlaceholder'

import {CLASSNAME, CLASSNAME_PREFIX, componentClassName, isRequiredClassName} from '../constants'

const fetchMatchingItems = (searchCallback, tokenProvider, profileApiUrl) =>
    debounce(
        (input) =>
            Promise.all([
                fetchGroups(input, null, null, null, null, tokenProvider, profileApiUrl),
                fetchUsers(input, null, null, tokenProvider, profileApiUrl)
            ]).then(([groups, users]) => {
                const options = _sortBy(_concat(groups, users), ['name', 'id'])
                return Promise.resolve(searchCallback(options))
            }),
        300
    )

const renderItem = (item, size, showLink, showMemberCount, tokenProvider, profileApiUrl) => {
    if (item.type === 'group') {
        return item.name ? (
            <PapiGroup
                group={item}
                size={size}
                showLink={showLink}
                showMemberCount={showMemberCount}
                tokenProvider={tokenProvider}
                profileApiUrl={profileApiUrl}
            />
        ) : (
            <PapiGroup
                id={item.id}
                size={size}
                showLink={showLink}
                showMemberCount={showMemberCount}
                tokenProvider={tokenProvider}
                profileApiUrl={profileApiUrl}
            />
        )
    }
    return item.profilePhotoSrc || item.name === 'DOES-NOT-EXIST' ? (
        <PapiUser user={item} size={size} showLink={showLink} active={item.active} />
    ) : (
        <PapiUser id={item.id} size={size} showLink={showLink} active={!!item.active} />
    )
}

const renderValue =
    (size, showLink, showMemberCount) =>
    ({children, data}) => {
        const foundOption = _.intersection(Object.keys(children.props), ['entity', 'user', 'group'])
        if (foundOption.length > 0) {
            return renderItem(children.props[foundOption[0]], size, showLink, showMemberCount)
        }

        return renderItem({id: data.id, type: data.type}, size, showLink, showMemberCount)
    }

const PapiUserOrGroupSelect = (props) => {
    const {
        title,
        outlined,
        value,
        isRequired,
        size,
        showLink = false,
        showMemberCount = false,
        showOptionLink = false,
        searchCallback = (items) => items,
        tokenProvider,
        profileApiUrl
    } = props
    const [originalPlaceholder] = useState(props.placeholder)

    const {isFocussed, placeholder, handleOnFocus, handleOnBlur} = useFocussedPlaceholder(
        false,
        originalPlaceholder || title,
        isRequired
    )
    useEffect(() => {
        const updateValue = async () => {
            if (!Array.isArray(value) && value.type === 'user') {
                props.onChange(await getUserById(value.id, true, tokenProvider, profileApiUrl))
            } else if (Array.isArray(value)) {
                props.onChange(
                    await Promise.all(
                        value.map(async (v) =>
                            v.type === 'user'
                                ? getUserById(v.id, true, tokenProvider, profileApiUrl)
                                : v
                        )
                    )
                )
            }
        }
        if (value) {
            updateValue()
        }
    }, [])

    return (
        <div className={`papi-react ${isRequiredClassName({isRequired, value})}`}>
            <div className={componentClassName(outlined)}>
                <FloatingLabel
                    title={title}
                    value={value}
                    isFocussed={isFocussed}
                    isRequired={isRequired}
                    hasCustomPlaceholder={!!originalPlaceholder}
                />
                <AsyncSelect
                    id="user-group-select"
                    className={CLASSNAME}
                    classNamePrefix={CLASSNAME_PREFIX}
                    placeholder={placeholder}
                    getOptionLabel={(option) =>
                        renderItem(
                            option,
                            size,
                            showOptionLink,
                            showMemberCount,
                            tokenProvider,
                            profileApiUrl
                        )
                    }
                    getOptionValue={(option) => option.id}
                    loadOptions={fetchMatchingItems(searchCallback, tokenProvider, profileApiUrl)}
                    openMenuOnFocus={false}
                    openMenuOnClick={false}
                    isClearable
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    cacheOptions
                    closeMenuOnSelect
                    components={{
                        LoadingIndicator,
                        SingleValue: renderValue(size, showLink, showMemberCount),
                        MultiValueLabel: renderValue(size, showLink, showMemberCount),
                        DropdownIndicator
                    }}
                    {...props}
                />
            </div>
        </div>
    )
}

PapiUserOrGroupSelect.displayName = 'PapiUserOrGroupSelect'

PapiUserOrGroupSelect.propTypes = {
    title: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    outlined: PropTypes.bool,
    isRequired: PropTypes.bool,
    isMulti: PropTypes.bool,
    showLink: PropTypes.bool,
    showMemberCount: PropTypes.bool,
    showOptionLink: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large', 'x-large']),
    searchCallback: PropTypes.func
}

export default PapiUserOrGroupSelect
